<template>
  <div>
    <SectionHeader
      :logo="BusinessStudioLogo"
      productName="Business Studio+"
      :title="$t('tbs_home_plus__promo')"
      :subtext="
        isCancelledSubscription
          ? $t('subscription_canceled_info', {
              EndDate:
                studioPlusCampaign?.endDate && !isNaN(new Date(studioPlusCampaign?.endDate))
                  ? formatDate(studioPlusCampaign.endDate, { timeZone: 'UTC' })
                  : '-',
            })
          : $t('subscription_start_date', {
              StartDate:
                studioPlusCampaign?.createdAt && !isNaN(new Date(studioPlusCampaign?.createdAt))
                  ? formatDate(studioPlusCampaign.createdAt, { timeZone: 'UTC' })
                  : '-',
            })
      "
    />
  </div>
  <div class="tw:mb-8">
    <StudioPlusProductSection :title="$t('tbs_mp_plus_benefits_1')" :isLast="false">
      <div>
        <PropertyHotelConnectBadge :hasCampaign="true" status="contact_badge_live" />
        <div
          class="tw:mt-1 tw:flex tw:flex-col tw:md:flex-row tw:justify-center tw:md:justify-between tw:md:items-center"
        >
          <p v-html="$t('hotel_connect_subtext')" />
          <CheckPropertyLink source="homepage" />
        </div>
      </div>
    </StudioPlusProductSection>
    <StudioPlusProductSection v-if="hasActiveOffer" :title="$t('tbs_mp_plus_benefits_4')" :isLast="false">
      <div class="tw:flex tw:flex-col tw:rounded-lg tw:border tw:border-gray-300">
        <ActiveOffer
          @openRemoveModal="openRemoveOfferModal"
          @openEditOffer="openEditOffer"
          :activeOffer="activeOffer"
          source="homepage"
        />
      </div>
    </StudioPlusProductSection>
    <HotelOffersPromoBanner v-else />
    <StudioPlusProductSection :title="$t('plus_premium_features_header')" :isLast="true">
      <div class="tw:flex tw:flex-col tw:gap-y-6">
        <div
          v-for="item in moreItems"
          :key="item.id"
          class="tw:flex tw:md:flex-row tw:flex-col tw:justify-between tw:md:items-center"
        >
          <div class="tw:flex tw:flex-row tw:gap-x-4 tw:items-start tw:md:items-center tw:md:mb-0 tw:mb-4">
            <img :src="item.icon" :alt="item.title" class="tw:hidden tw:md:flex" />
            <img :src="item.mobileIcon" :alt="item.title" class="tw:flex tw:md:hidden" />
            <div class="tw:flex tw:flex-col tw:gap-y-1">
              <h2 class="tw:text-xl tw:font-bold">{{ $t(item.title) }}</h2>
              <p class="tw:text-sm">{{ $t(item.subtitle) }}</p>
              <router-link
                @click="triggerEvent(item.eventName)"
                :to="item.link"
                class="tw:md:hidden tw:flex tw:text-blue-700 tw:text-sm tw:font-bold tw:hover:underline tw:hover:text-blue-800"
                >{{ $t(item.buttonLabel) }}</router-link
              >
            </div>
          </div>
          <div class="tw:w-1/3 tw:hidden tw:md:flex tw:justify-end">
            <router-link
              @click="triggerEvent(item.eventName)"
              :to="item.link"
              class="tw:text-blue-700 tw:text-sm tw:font-bold tw:hover:underline tw:hover:text-blue-800"
              >{{ $t(item.buttonLabel) }}</router-link
            >
          </div>
        </div>
      </div>
    </StudioPlusProductSection>

    <div v-if="isCancelledSubscription && !isPro" class="tw:my-6">
      <div class="tw:flex tw:justify-between tw:md:items-end tw:md:flex-row tw:flex-col">
        <div class="tw:mb-4 tw:md:mb-0">
          <p v-if="!isPriceLoading" class="tw:font-bold tw:text-2xl">
            {{ formatCurrency(price) }}
          </p>
          <SmallDotLoader v-else color="#B4183A" />
          <p class="tw:text-gray-700 tw:text-xs">
            {{ $t('check_out_billing_info_subscription')
            }}<span class="tw:ml-1">{{ $t('tbs_home_plus_tax_disclaimer') }}</span>
          </p>
        </div>
        <div class="tw:flex tw:flex-col tw:gap-y-2 tw:md:flex-row tw:gap-x-2.5">
          <GhostButton
            class="icon-btn tw:flex tw:gap-x-2 tw:justify-center tw:items-center tw:mb-2 tw:sm:mb-0"
            @click="openVideo"
          >
            <PlayIcon class="tw:fill-gray-800" />
            {{ $t('promo_video_cta') }}
          </GhostButton>
          <MainButton @click="reactivatePlus">{{
            hasAdminOrSupport ? $t('reactivate_subscription_cta') : $t('rc_admin_log_in_cta')
          }}</MainButton>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'

import CheckPropertyLink from '@/components/dashboard/CheckPropertyLink.vue'
import SectionHeader from '@/components/homePage/SectionHeader.vue'
import StudioPlusProductSection from '@/components/homePage/studioPlus/StudioPlusProductSection.vue'
import HotelOffersPromoBanner from '@/components/hotelOffers/HotelOffersPromoBanner.vue'
import PropertyHotelConnectBadge from '@/components/propertyDetails/propertyBasicInfo/PropertyHotelConnectBadge.vue'
import ActiveOffer from '@/components/hotelOffers/ActiveOffer.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import PlayIcon from '@/components/icons/PlayIcon.svg?component'
import MainButton from '@/components/base/buttons/MainButton.vue'

import { selectedAccommodationId } from '@/layouts/queries'
import { useActiveOffer } from '@/components/hotelOffers/queries'
import { useHotelOffers } from '@/components/hotelOffers/useHotelOffers'
import { useStudioPlusCampaign, useStudioPlusPrice } from '@/components/payment/queries'

import appAnalytics from '@/utils/tracking'
import { useSessionValues } from '@/composables/useSessionValues'
import { useFormatting } from '@/composables/useFormatting'

const BusinessStudioLogo = new URL('/src/assets/img/landing/landing_studio_plus.svg', import.meta.url)

const store = useStore()

const { currency, hasAdminOrSupport } = useSessionValues()
const { formatCurrency, formatDate } = useFormatting()
const { openRemoveOfferModal, openEditOffer } = useHotelOffers()
const { hasActiveOffer, activeOffer } = useActiveOffer(selectedAccommodationId)
const { isCancelledSubscription, studioPlusCampaign, isPro } = useStudioPlusCampaign(selectedAccommodationId)
const { studioPlusPrice, isLoading, isFetching } = useStudioPlusPrice({ selectedAccommodationId, currency })
const price = computed(() => studioPlusPrice.value?.baseSubscriptionMonthlyPrice ?? 0)
const isPriceLoading = computed(() => isLoading.value || isFetching.value)

const RateInsights = new URL('/src/assets/img/homepage/DashboardIcon.svg', import.meta.url)
const TravelerTrends = new URL('/src/assets/img/homepage/BarsIcon.svg', import.meta.url)
const RateInsightsMobile = new URL('/src/assets/img/homepage/DashboardIconMobile.svg', import.meta.url)
const TravelerTrendsMobile = new URL('/src/assets/img/homepage/BarsIconMobile.svg', import.meta.url)

const moreItems = [
  {
    id: 0,
    title: 'studio_plus_mp_rateinsights',
    subtitle: 'tbs_mp_plus_benefits_3_text',
    icon: RateInsights,
    mobileIcon: RateInsightsMobile,
    link: { name: 'rate-insights' },
    buttonLabel: 'rate_insights_cta',
    eventName: appAnalytics.events.HOMEPAGE_RI_CLICKED,
  },
  {
    id: 1,
    title: 'studio_plus_mp_visitorsprofile',
    subtitle: 'tbs_mp_plus_benefits_2_text',
    icon: TravelerTrends,
    mobileIcon: TravelerTrendsMobile,
    link: { name: 'visitors-profile-stay-duration' },
    buttonLabel: 'visitors_profile_cta',
    eventName: appAnalytics.events.HOMEPAGE_VP_CLICKED,
  },
]

const emit = defineEmits(['openVideo', 'openReactivationFlow'])
const openVideo = () => emit('openVideo')

const triggerEvent = eventName => {
  appAnalytics.track(eventName, { accommodationId: selectedAccommodationId.value })
}
const reactivatePlus = () => {
  if (hasAdminOrSupport.value) {
    emit('openReactivationFlow')
  } else {
    store.dispatch('session/logout', '/?loginAsAdmin=true&isReactivationOpen=true')
  }
}
</script>
<style scoped>
.icon-btn:hover svg {
  fill: white;
}
</style>
